'use client';

import MinusCircleSvg from '@/assets/svgs/jobs/minus-circle.svg';
import { inter600 } from '@/config/fonts';
import {
  selectQuestions,
  selectReplyTemplates,
} from '@/containers/Jobs/redux/jobEditors.selectors';
import { addNewQuestion, updateQuestion } from '@/containers/Jobs/redux/jobEditors.slice';
import { JobQuestion } from '@/containers/Jobs/types';
import { useAppDispatch } from '@/lib/redux/store';
import IconButton from '@/shared/components/Buttons/IconButton/IconButton';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

interface QuestionFormProps {
  postSubmit: () => void;
  jobId: string;
  questionId?: string;
}

interface ResponseOption {
  text: string;
  template: string;
}

import { CONTROL_TYPE_OPTIONS } from '@/containers/Jobs/utils/questionTypes';

const QuestionForm = ({ postSubmit, jobId, questionId }: QuestionFormProps) => {
  const dispatch = useAppDispatch();
  const questions = useSelector(selectQuestions(jobId));
  const replyTemplates = useSelector(selectReplyTemplates(jobId));

  const existingQuestion = questionId
    ? questions.find((q) => q.id === questionId)
    : undefined;

  const isEditing = !!questionId;

  const [formData, setFormData] = useState<{
    controlType: 'number' | 'text' | 'select';
    title: string;
    template: string;
    responses: ResponseOption[];
  }>({
    controlType: 'select',
    title: '',
    template: '',
    responses: [],
  });

  const [errors, setErrors] = useState<{
    title?: string;
    template?: string;
    responses?: Array<{ text?: string; template?: string }>;
    responsesGeneral?: string;
  }>({});

  // Populate form when editing
  useEffect(() => {
    if (existingQuestion) {
      setFormData({
        controlType: existingQuestion.controlType,
        title: existingQuestion.title,
        template: existingQuestion.template,
        responses: existingQuestion.responses.map((r) => ({
          text: r.text,
          template: r.template,
        })),
      });
    }
  }, [existingQuestion]);

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Question text is required';
    }

    // Reply template is optional for non-select questions

    if (formData.controlType === 'select') {
      // A dropdown question must have at least one response option
      if (formData.responses.length === 0) {
        newErrors.responsesGeneral = 'Add at least one response option';
      }

      newErrors.responses = [];
      formData.responses.forEach((response, index) => {
        const responseErrors: { text?: string; template?: string } = {};

        if (!response.text.trim()) {
          responseErrors.text = 'Response text is required';
        }

        // Reply template is optional for response options

        newErrors.responses![index] = responseErrors;
      });

      // Remove empty error objects
      newErrors.responses = newErrors.responses.filter((err) => err.text);

      if (newErrors.responses.length === 0) {
        delete newErrors.responses;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const questionData: Partial<JobQuestion> = {
      controlType: formData.controlType,
      title: formData.title,
      van_question: null,
      template: formData.controlType === 'select' ? '' : formData.template,
      responses:
        formData.controlType === 'select'
          ? formData.responses.map((r) => ({
              id: uuidv4(),
              text: r.text,
              template: r.template,
              integration_details: {},
            }))
          : [],
    };

    if (isEditing && questionId) {
      dispatch(
        updateQuestion({
          jobId,
          id: questionId,
          props: questionData,
        })
      );
    } else {
      dispatch(
        addNewQuestion({
          jobId,
          question: questionData,
        })
      );
    }

    postSubmit();
  };

  const addResponseOption = () => {
    setFormData((prev) => ({
      ...prev,
      responses: [...prev.responses, { text: '', template: '' }],
    }));
  };

  const removeResponseOption = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      responses: prev.responses.filter((_, i) => i !== index),
    }));
  };

  const updateResponseOption = (
    index: number,
    field: 'text' | 'template',
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      responses: prev.responses.map((response, i) =>
        i === index ? { ...response, [field]: value } : response
      ),
    }));
  };

  return (
    <div className="pb-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Question Type */}
        <div>
          <label className={classNames('mb-2 block text-sm', inter600.className)}>
            Question Type
          </label>
          <select
            value={formData.controlType}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                controlType: e.target.value as 'number' | 'text' | 'select',
                responses: e.target.value === 'select' ? prev.responses : [],
              }))
            }
            className="select select-bordered w-full">
            {CONTROL_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Question Text */}
        <div>
          <label className={classNames('mb-2 block text-sm', inter600.className)}>
            Question Text
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
            className="input input-bordered w-full"
            placeholder="Enter your question text"
          />
          {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
        </div>

        {/* Conditional fields based on control type */}
        {formData.controlType !== 'select' && (
          <div>
            <label className={classNames('mb-2 block text-sm', inter600.className)}>
              Linked Reply Template
            </label>
            <select
              value={formData.template}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, template: e.target.value }))
              }
              className="select select-bordered w-full">
              <option value="">Select a template</option>
              {replyTemplates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.title}
                </option>
              ))}
            </select>
            {errors.template && (
              <p className="mt-1 text-sm text-red-500">{errors.template}</p>
            )}
          </div>
        )}

        {/* Response Options for 'select' type */}
        {formData.controlType === 'select' && (
          <div>
            <div className="mb-4 flex items-center justify-between">
              <label className={classNames('block text-sm', inter600.className)}>
                Response Options
              </label>
              <button
                type="button"
                onClick={addResponseOption}
                className="btn btn-outline btn-sm">
                Add Option
              </button>
            </div>

            <div className="space-y-4">
              {formData.responses.map((response, index) => (
                <div key={index} className="rounded-lg border p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <span className={classNames('text-sm', inter600.className)}>
                      Option {index + 1}
                    </span>
                    <IconButton
                      className="btn-outline border-blue-200 shadow-sm"
                      title="Remove"
                      icon={<MinusCircleSvg />}
                      onClick={() => removeResponseOption(index)}
                    />
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="mb-1 block text-xs">Response Text</label>
                      <input
                        type="text"
                        value={response.text}
                        onChange={(e) =>
                          updateResponseOption(index, 'text', e.target.value)
                        }
                        className="input input-sm input-bordered w-full"
                        placeholder="Enter response option"
                      />
                      {errors.responses?.[index]?.text && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.responses[index]?.text}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="mb-1 block text-xs">Linked Reply Template</label>
                      <select
                        value={response.template}
                        onChange={(e) =>
                          updateResponseOption(index, 'template', e.target.value)
                        }
                        className="select select-bordered select-sm w-full">
                        <option value="">Select a template</option>
                        {replyTemplates.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.title}
                          </option>
                        ))}
                      </select>
                      {errors.responses?.[index]?.template && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.responses[index]?.template}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {formData.responses.length === 0 && (
                <div className="py-8 text-center text-gray-500">
                  <p>No response options added yet.</p>
                  <p className="text-sm">
                    Click &quot;Add Option&quot; to create response choices.
                  </p>
                  {errors.responsesGeneral && (
                    <p className="mt-2 text-xs text-red-500">{errors.responsesGeneral}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end gap-3 border-t pt-4">
          <button type="button" onClick={postSubmit} className="btn btn-outline">
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            {isEditing ? 'Update Question' : 'Add Question'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuestionForm;
